import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const PopoverRoot = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = forwardRef<ElementRef<typeof PopoverPrimitive.Content>, ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>>(
    ({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
                ref={ref}
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    // Above the Radix dialog tier (overlay 4000 / content 4001):
                    // a popover is a transient surface anchored to whatever opened
                    // it, so it must paint on top even when its trigger sits inside
                    // a modal (the casting dialog's selects rendered UNDER the
                    // overlay at the old MUI tier 1300 and looked like they never
                    // opened). Callers can still override via className (twMerge).
                    'z-[4100] rounded-2xl bg-bg-paper p-4 shadow-md outline-none',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    className,
                )}
                {...props}
            />
        </PopoverPrimitive.Portal>
    ),
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

/** MUI Paper's elevation-8 shadow — what every MUI Popover paper cast. */
const ELEVATION_8 = 'shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.2),0px_8px_10px_1px_rgba(0,0,0,0.14),0px_3px_14px_2px_rgba(0,0,0,0.12)]';

interface PopoverProps extends Omit<ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, 'content'> {
    /** The element to position against (MUI-compatible anchor state). */
    anchorEl: Element | null | undefined;
    open: boolean;
    onClose: () => void;
    /** Hover-driven popovers: no focus steal on open, no focus restore on close. */
    disableRestoreFocus?: boolean;
    /** MUI parity default true: outside click closes and is swallowed, scroll locks. */
    modal?: boolean;
    children: ReactNode;
}

/** The app-wide anchored popover. MUI-shaped interface (anchorEl/open/onClose)
 *  over the Radix compound above; positioning is expressed as Radix side/align
 *  (+offsets) instead of MUI origin pairs. Defaults mirror what MUI gave every
 *  popover implicitly: c24 paper (MuiPaper override), 4px radius, elevation-8
 *  shadow, no padding. */
function Popover({ anchorEl, open, onClose, disableRestoreFocus, modal = true, className, children, ...contentProps }: PopoverProps) {
    return (
        <PopoverRoot
            open={open}
            modal={modal}
            onOpenChange={nextOpen => {
                if (!nextOpen) onClose();
            }}
        >
            {anchorEl && <PopoverAnchor virtualRef={{ current: anchorEl }} />}
            <PopoverContent
                sideOffset={0}
                className={cn('rounded bg-c24 p-0', ELEVATION_8, className)}
                onOpenAutoFocus={disableRestoreFocus ? event => event.preventDefault() : undefined}
                onCloseAutoFocus={event => {
                    event.preventDefault();
                    if (!disableRestoreFocus && anchorEl instanceof HTMLElement) anchorEl.focus();
                }}
                {...contentProps}
            >
                {children}
            </PopoverContent>
        </PopoverRoot>
    );
}

export { Popover, PopoverAnchor, PopoverContent, PopoverRoot, PopoverTrigger };
